import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { twMerge } from "tailwind-merge";
import { type ClassValue, clsx } from "clsx";
dayjs.extend(relativeTime);
import { publicRoutes } from "./../auth/routes";

/**
 * Checks whether a value is null or undefined. If it is not defined, the return type ensures type safety.
 * @param value any value or null or undefined
 * @returns true if `value` is null or undefined, false otherwise.
 */
export function isNullOrUndefined(value: unknown) {
  return value === null || value === undefined;
}

/**
 * Checks whether a value is defined. If it is defined, the return type ensures type safety.
 * @param value any value or null or undefined
 * @returns true if `value` is defined (not null and not undefined), false otherwise.
 */
export function isDefined(value: unknown) {
  return !isNullOrUndefined(value);
}

export function getCurrentTime() {
  return dayjs().format("h:mmA | MMMM D, YYYY");
}

export function encodePhone(phone: string) {
  return encodeURIComponent(phone).replace(/%20/g, "+");
}

export function formatCurrency(
  amount: number,
  currency?: "Naira" | "Yuan" | "Dollar",
): string {
  const currencySymbol =
    currency === "Yuan" ? "¥" : currency === "Dollar" ? "$" : "₦";
  const nairaAmount = amount / 100; // Convert from kobo to naira
  return `${currencySymbol}${nairaAmount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function calculateDaysLeft(endDate: string) {
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? `${diffDays} days` : "Expired";
}

export function formatDate(dateString: string) {
  return dayjs(dateString).format("MMMM D, YYYY");
}

export function formatDateFromNow(dateString: string) {
  return dayjs(dateString).fromNow();
}

export function formatTime(dateString: string) {
  return dayjs(dateString).format("h:mm A");
}

export function formatDateTime(dateString: string) {
  return dayjs(dateString).format("DD/MM/YY h:mm A");
}

export function formatDateTimeFull(dateString: string) {
  return dayjs(dateString).format("MMMM D, YYYY h:mm A");
}

function getOrdinalSuffix(day: number) {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

export function formatDateSuffix(dateString: string, showYear?: boolean) {
  const day = dayjs(dateString).date();
  const month = dayjs(dateString).format("MMMM");
  const year = dayjs(dateString).format("YYYY");
  if (showYear) {
    return `${month} ${day}${getOrdinalSuffix(day)}${`, ${year}`}`;
  } else return `${month} ${day}${getOrdinalSuffix(day)}`;
}

export function appendCurrentTime(dateString: string) {
  return `${dateString}${dayjs().format("HH:mm:ssZ")}`;
}

export function containsOnlyNumbers(s: string) {
  return /^\d+$/.test(s);
}

export function getErrorMessage(res: any) {
  let message;
  if (res?.error?.message) {
    const errorMessage = res.error.message;
    if (typeof errorMessage === "string") {
      message = errorMessage;
    } else if (typeof errorMessage === "object") {
      message = JSON.stringify(Object.values(errorMessage));
    }
  }
  return message;
}

export function formatArrayToText(array: string[]) {
  if (array.length === 0) return "";
  if (array.length === 1) return array[0];
  if (array.length === 2) return array.join(" & ");
  return array.slice(0, -1).join(", ") + " & " + array[array.length - 1];
}

export function minVersionCheck(
  version: string,
  majorVersion: number,
  minorVersion: number,
) {
  const [osMajorVersion, osMinorVersion] = version.split(".");
  if (
    Number(osMajorVersion) < majorVersion ||
    (Number(osMajorVersion) === majorVersion &&
      Number(osMinorVersion) < minorVersion)
  ) {
    return false;
  } else {
    return true;
  }
}

export const convertToKobo = (amount?: number) => {
  return amount ? amount * 100 : 0;
};

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractDomain(url: string) {
  const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^/]+)/);
  return match ? match[1] : url;
}

export function extractProductUrl(value?: string | null) {
  const input = value?.trim();
  if (!input) return undefined;

  const httpUrl = input.match(/https?:\/\/[^\s<>"'`]+/i)?.[0];
  const hostname = httpUrl
    ? undefined
    : input.match(
        /(?:www\.)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>"'`]*)?/i,
      )?.[0];
  const candidate = (httpUrl ?? (hostname ? `https://${hostname}` : "")).replace(
    /[\])}>,.!?;:'"。，！？；：、】》]+$/u,
    "",
  );

  try {
    const url = new URL(candidate);
    return url.protocol === "http:" || url.protocol === "https:"
      ? candidate
      : undefined;
  } catch {
    return undefined;
  }
}

export function isPublic(pathname: string): boolean {
  return publicRoutes.some((route) => {
    const regex = new RegExp(`^${route.replace(/:id/g, "[^/]+")}$`);
    return regex.test(pathname);
  });
}

// Image proxy utilities
export {
  getImageUrl,
  getProxiedImageUrl,
  needsImageProxy,
} from "./image-proxy";
